<template>
    <div class="password-recovery bg-light min-vh-100">
        <div class="bg-white border-bottom">
            <div class="container py-3">
                <div class="d-flex align-items-center justify-content-between">
                    <div class="d-flex align-items-center">
                        <img
                            src="https://getasia.s3.ap-southeast-1.amazonaws.com/images/hextar_logo.png"
                            class="recovery-logo"
                            alt="Hextar"
                        >
                        <div class="border-left ml-3 pl-3">
                            <h5 class="font-weight-bold mb-0">Account Access</h5>
                            <small class="text-muted">Get back into your account securely</small>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="bg-info py-4">
            <div class="container text-white">
                <h3 class="text-white mt-2 mb-1">
                    Account access
                </h3>
                <p class="mb-0">
                    Reset your password or securely sign in using a verification code sent to your registered email address.
                </p>
            </div>
        </div>
        <div class="container py-4 py-md-5">
            <div class="row justify-content-center">
                <div class="col-md-9 col-lg-7 col-xl-6">
                    <div class="card border-0 shadow-sm mb-3">
                        <div class="card-body p-3 p-md-4">
                            <div class="row mx-n1">
                                <div class="col-6 px-1">
                                    <b-button
                                        type="button"
                                        variant="light"
                                        class="w-100 h-100 p-3 text-left rounded border border-light"
                                        :class="accountType == 'admin' ? 'border-info bg-soft-info shadow-lg' : 'border-light bg-white shadow'"
                                        :disabled="!apiStatus.save || !appStatus.save"
                                        :aria-pressed="accountType == 'admin'"
                                        @click="changeAccountType('admin')"
                                    >
                                        <div class="d-flex align-items-center justify-content-between mb-2">
                                            <i class="mdi mdi-shield-account-outline font-size-24 text-info"></i>
                                            <i v-if="accountType == 'admin'" class="mdi mdi-check-circle text-info font-size-18"></i>
                                        </div>
                                        <strong class="d-block">Admin</strong>
                                        <small class="text-muted">Administration</small>
                                    </b-button>
                                </div>
                                <div class="col-6 px-1">
                                    <b-button
                                        type="button"
                                        variant="light"
                                        class="w-100 h-100 p-3 text-left rounded border border-light"
                                        :class="accountType == 'ihextar' ? 'border-info bg-soft-info shadow-lg' : 'border-light bg-white shadow'"
                                        :disabled="!apiStatus.save || !appStatus.save"
                                        :aria-pressed="accountType == 'ihextar'"
                                        @click="changeAccountType('ihextar')"
                                    >
                                        <div class="d-flex align-items-center justify-content-between mb-2">
                                            <i class="mdi mdi-account-circle-outline font-size-24 text-info"></i>
                                            <i v-if="accountType == 'ihextar'" class="mdi mdi-check-circle text-info font-size-18"></i>
                                        </div>
                                        <strong class="d-block">iHextar</strong>
                                        <small class="text-muted">Employee</small>
                                    </b-button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="card border-0 shadow-sm mb-3">
                        <div class="card-body p-4 p-md-5">
                            <template v-if="!['admin', 'ihextar'].includes(accountType)">
                                <div class="text-center py-3">
                                    <i class="mdi mdi-information-outline text-info recovery-icon"></i>
                                    <h4 class="mt-3">Account recovery is unavailable</h4>
                                    <p class="text-muted">Please return to login. Recovery for this account type is not available yet.</p>
                                    <router-link to="/login" class="btn btn-info">Back to login</router-link>
                                </div>
                            </template>
                            <div v-else-if="step == 'complete'" class="text-center py-3 fade-in" role="status">
                                <i class="mdi mdi-check-circle-outline text-success recovery-icon"></i>
                                <h4 class="mt-3">Password reset successful</h4>
                                <p class="text-muted mb-4">Your password has been updated. You can now sign in with your new password.</p>
                                <router-link to="/login" class="btn btn-info px-4">Back to login</router-link>
                            </div>
                            <form v-else :key="step" @submit.prevent="requestSubmit" novalidate>
                                <div class="d-flex align-items-center mb-3 fade-in" :key="mode">
                                    <div class="recovery-symbol bg-soft-info text-info rounded-circle mr-3">
                                        <i :class="mode == 'reset' ? 'mdi mdi-lock-reset' : 'mdi mdi-email-check-outline'"></i>
                                    </div>
                                    <div>
                                        <h4 class="mb-1">{{ mode == 'reset' ? 'Reset password' : 'Login by OTP' }}</h4>
                                        <small v-if="step == 'email'" class="text-muted">Choose how you would like to access your account</small>
                                        <small v-else-if="step == 'otp' && mode == 'reset'" class="text-muted">Step 2 of 3 · Verify your email</small>
                                        <small v-else-if="step == 'otp'" class="text-muted">Step 2 of 2 · Verify your email</small>
                                        <small v-else-if="step == 'password'" class="text-muted">Step 3 of 3 · Choose a new password</small>
                                    </div>
                                </div>
                                <ul v-if="step == 'email'" class="nav nav-tabs nav-justified mb-4">
                                    <li class="nav-item">
                                        <button
                                            type="button"
                                            class="nav-link w-100 h-100"
                                            :class="mode == 'reset' ? 'active text-info font-weight-bold fade-in' : 'text-muted'"
                                            :disabled="!status.save"
                                            :aria-pressed="mode == 'reset'"
                                            @click="changeMode('reset')"
                                        >
                                            <i class="mdi mdi-lock-reset mr-1"></i>Reset password
                                        </button>
                                    </li>
                                    <li class="nav-item">
                                        <button
                                            type="button"
                                            class="nav-link w-100 h-100"
                                            :class="mode == 'login' ? 'active text-info font-weight-bold fade-in' : 'text-muted'"
                                            :disabled="!status.save"
                                            :aria-pressed="mode == 'login'"
                                            @click="changeMode('login')"
                                        >
                                            <i class="mdi mdi-email-check-outline mr-1"></i>Login by OTP
                                        </button>
                                    </li>
                                </ul>
                                <b-form-group :label="$t('label.email')" label-for="recovery-email">
                                    <b-form-input
                                        id="recovery-email"
                                        v-model.trim="createForm.email"
                                        type="email"
                                        autocomplete="email"
                                        :disabled="step != 'email' || !status.save"
                                        :class="[
                                            step != 'email' || !status.save ? 'bg-light' : '',
                                            { 'is-invalid': submitted && $v.createForm.email.$error },
                                        ]"
                                    />
                                    <b-form-invalid-feedback v-if="submitted && $v.createForm.email.$error" class="text-right">
                                        <span v-if="!$v.createForm.email.required">{{ $t('message.requiredX', [$t('label.email')]) }}</span>
                                        <span v-else>{{ $t('message.validX', [$t('label.email')]) }}</span>
                                    </b-form-invalid-feedback>
                                </b-form-group>
                                <template v-if="step == 'otp'">
                                    <b-alert show variant="info" class="small">We have sent a 6-digit verification code to your email. Enter it below to continue.</b-alert>
                                    <b-form-group label="Verification code" label-for="recovery-otp-0">
                                        <div class="d-flex mx-n1 recovery-otp" @paste.prevent="pasteOtp">
                                            <b-form-input
                                                v-for="(digit, index) in otpDigits"
                                                :key="index"
                                                :id="'recovery-otp-' + index"
                                                ref="otpInputs"
                                                :value="digit"
                                                type="text"
                                                inputmode="numeric"
                                                pattern="[0-9]*"
                                                :autocomplete="index == 0 ? 'one-time-code' : 'off'"
                                                maxlength="1"
                                                :aria-label="'Verification code digit ' + (index + 1)"
                                                :disabled="!status.save"
                                                :readonly="index != currentOtpIndex"
                                                :tabindex="index == currentOtpIndex ? 0 : -1"
                                                :class="[
                                                    'text-center text-info font-weight-bold font-size-24 bg-light rounded p-0 mx-1',
                                                    submitted && $v.createForm.otp.$error ? 'is-invalid' : '',
                                                    digit && !(submitted && $v.createForm.otp.$error) ? 'border-info' : '',
                                                ]"
                                                @input="setOtpDigit(index, $event)"
                                                @beforeinput.native="numbersOnly($event)"
                                                @input.native="$event.target.value = $event.target.value.replace(/\D/g, '').slice(-1)"
                                                @focus="focusOtp(index)"
                                                @keydown.backspace.prevent="backspaceOtp(index)"
                                            />
                                        </div>
                                        <small class="d-block text-center text-muted mt-3">Enter the 6 digits from your email</small>
                                        <div v-if="submitted && $v.createForm.otp.$error" class="invalid-feedback d-block text-right">Enter the 6-digit verification code.</div>
                                    </b-form-group>
                                </template>
                                <template v-if="step == 'password'">
                                    <b-alert show variant="success" class="small">Email verified. Set a new password for your account.</b-alert>
                                    <b-form-group label="New password" label-for="recovery-password">
                                        <b-input-group>
                                            <b-form-input
                                                id="recovery-password"
                                                v-model="createForm.password"
                                                :type="showPassword ? 'text' : 'password'"
                                                autocomplete="new-password"
                                                :disabled="!status.save"
                                                :class="{ 'is-invalid': submitted && $v.createForm.password.$error }"
                                            />
                                            <b-input-group-append>
                                                <b-input-group-text class="cursor-pointer" @click="showPassword = !showPassword">
                                                    <i :class="showPassword ? 'mdi mdi-eye' : 'mdi mdi-eye-off'"></i>
                                                </b-input-group-text>
                                            </b-input-group-append>
                                        </b-input-group>
                                        <b-form-invalid-feedback v-if="submitted && $v.createForm.password.$error" class="d-block text-right">{{ $t('message.requiredX', [$t('label.password')]) }}</b-form-invalid-feedback>
                                    </b-form-group>
                                    <b-form-group label="Confirm password" label-for="recovery-confirm-password">
                                        <b-input-group>
                                            <b-form-input
                                                id="recovery-confirm-password"
                                                v-model="createForm.confirmPassword"
                                                :type="showConfirmPassword ? 'text' : 'password'"
                                                autocomplete="new-password"
                                                :disabled="!status.save"
                                                :class="{ 'is-invalid': submitted && $v.createForm.confirmPassword.$error }"
                                            />
                                            <b-input-group-append>
                                                <b-input-group-text class="cursor-pointer" @click="showConfirmPassword = !showConfirmPassword">
                                                    <i :class="showConfirmPassword ? 'mdi mdi-eye' : 'mdi mdi-eye-off'"></i>
                                                </b-input-group-text>
                                            </b-input-group-append>
                                        </b-input-group>
                                        <b-form-invalid-feedback v-if="submitted && $v.createForm.confirmPassword.$error" class="d-block text-right">Enter the same password in both fields.</b-form-invalid-feedback>
                                    </b-form-group>
                                </template>
                                <b-button type="submit" variant="info" class="w-100 mt-2 py-2" :disabled="!status.save">
                                    <span v-if="status.save">
                                        <span v-if="step == 'email'">Send verification code</span>
                                        <span v-else-if="step == 'otp' && mode == 'reset'">Verify code</span>
                                        <span v-else-if="step == 'otp'">Verify and log in</span>
                                        <span v-else-if="step == 'password'">Reset password</span>
                                        <i class="mdi mdi-arrow-right ml-2"></i>
                                    </span>
                                    <b-spinner v-else small />
                                </b-button>
                                <b-button
                                    v-if="step != 'email'"
                                    type="button"
                                    variant="link"
                                    class="w-100 text-muted mt-2"
                                    :disabled="!status.save"
                                    @click.prevent="startAgain"
                                >Use a different email or sign-in method</b-button>
                            </form>
                        </div>
                    </div>
                    <div class="text-center">
                        <router-link to="/login" class="text-muted"><i class="mdi mdi-arrow-left mr-1"></i>Back to login</router-link>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import { required, email, sameAs } from "vuelidate/lib/validators";
import { mapState } from "vuex";

export default {
    validations() {
        let createForm = {
            email: { required, email },
        };
        if (this.step == 'otp') {
            createForm.otp = { required, sixDigits: value => /^\d{6}$/.test(value) };
        }
        if (this.step == 'password') {
            createForm.password = { required };
            createForm.confirmPassword = { required, sameAsPassword: sameAs('password') };
        }
        return { createForm };
    },
    data() {
        return {
            showPassword: false,
            showConfirmPassword: false,
            mode: 'reset',
            step: 'email',
            submitted: false,
            otpDigits: ['', '', '', '', '', ''],
            createForm: this.defaultForm(),
        };
    },
    watch: {
        '$route.query.login'() {
            this.startAgain();
        },
    },
    methods: {
        changeAccountType(accountType) {
            if (accountType == this.accountType || !this.apiStatus.save || !this.appStatus.save) return;
            return this.$router.push({
                path: this.$route.path,
                query: { ...this.$route.query, login: accountType },
            });
        },
        defaultForm() {
            return {
                email: '',
                otp: '',
                userUuid: '',
                password: '',
                confirmPassword: '',
            };
        },
        changeMode(mode) {
            this.mode = mode;
            this.submitted = false;
            this.$v.$reset();
        },
        startAgain() {
            this.showPassword = false;
            this.showConfirmPassword = false;
            this.submitted = false;
            this.step = 'email';
            this.mode = 'reset';
            this.createForm = this.defaultForm();
            this.otpDigits = ['', '', '', '', '', ''];
            this.$nextTick(() => {
                this.$v.$reset();
            });
        },
        numbersOnly(event) {
            if (event.data && /\D/.test(event.data)) event.preventDefault();
        },
        setOtpDigit(index, value) {
            if (index != this.currentOtpIndex) return;
            const digit = String(value).replace(/\D/g, '').slice(-1);
            this.$set(this.otpDigits, index, digit);
            this.createForm.otp = this.otpDigits.join('');
            if (digit) this.$nextTick(() => this.$refs.otpInputs[this.currentOtpIndex].focus());
        },
        focusOtp(index) {
            if (index != this.currentOtpIndex) this.$refs.otpInputs[this.currentOtpIndex].focus();
        },
        backspaceOtp(index) {
            if (index != this.currentOtpIndex) return;
            const previousIndex = this.otpDigits[index] ? index : Math.max(index - 1, 0);
            this.$set(this.otpDigits, previousIndex, '');
            this.createForm.otp = this.otpDigits.join('');
            this.$nextTick(() => this.$refs.otpInputs[this.currentOtpIndex].focus());
        },
        pasteOtp(event) {
            const digits = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
            this.otpDigits = Array.from({length: 6}, (_, index) => digits[index] || '');
            this.createForm.otp = this.otpDigits.join('');
            this.$nextTick(() => this.$refs.otpInputs[this.currentOtpIndex].focus());
        },
        requestSubmit() {
            if (!['admin', 'ihextar'].includes(this.accountType) || !this.status.save) return;
            this.submitted = true;
            this.$v.$touch();
            if (this.$v.$invalid) return;
            if (this.step == 'email') return this.requestOtp();
            if (this.step == 'otp') return this.requestVerify();
            if (this.step == 'password') return this.requestReset();
        },
        requestOtp() {
            let data = { email: this.createForm.email };
            const namespace = this.accountType == 'admin' ? 'api/auth' : 'app/auth';
            const action = this.mode == 'reset' ? 'forgotPasswordOtpRequest' : 'loginOtpRequest';
            return this.$store.dispatch(`${namespace}/${action}`, {data})
                .then((result) => this.$nextTick(() => {
                    if (result?.success) {
                        this.createForm.userUuid = result.data?.user_uuid || '';
                        this.step = 'otp';
                        this.submitted = false;
                        this.$v.$reset();
                        this.$nextTick(() => this.$refs.otpInputs[0].focus());
                    }
                }));
        },
        requestVerify() {
            let data = {
                email: this.createForm.email,
                otp: this.createForm.otp,
            };
            const namespace = this.accountType == 'admin' ? 'api/auth' : 'app/auth';
            const action = this.mode == 'reset' ? 'forgotPasswordOtpVerify' : 'loginOtpVerify';
            return this.$store.dispatch(`${namespace}/${action}`, {data})
                .then((result) => this.$nextTick(() => {
                    if (result?.success && this.mode == 'reset') {
                        this.step = 'password';
                        this.submitted = false;
                        this.$v.$reset();
                    }
                }));
        },
        requestReset() {
            let data = {
                userUuid: this.createForm.userUuid,
                otp: this.createForm.otp,
                password: this.createForm.password,
            };
            const namespace = this.accountType == 'admin' ? 'api/auth' : 'app/auth';
            return this.$store.dispatch(`${namespace}/resetPasswordVerify`, {data})
                .then((result) => this.$nextTick(() => {
                    if (result?.success) {
                        this.step = 'complete';
                        this.createForm = this.defaultForm();
                        this.otpDigits = ['', '', '', '', '', ''];
                        this.submitted = false;
                        this.$v.$reset();
                    }
                }));
        },
    },
    computed: {
        currentOtpIndex() {
            const index = this.otpDigits.findIndex(digit => !digit);
            return index == -1 ? 5 : index;
        },
        ...mapState('api/auth', { apiStatus: 'status' }),
        ...mapState('app/auth', { appStatus: 'status' }),
        status() {
            return this.accountType == 'admin' ? this.apiStatus : this.appStatus;
        },
        accountType() {
            return this.$route.query.login || 'admin';
        },
    },
};
</script>
<style scoped>
.recovery-logo {
    height: 48px;
    width: auto;
}
.recovery-symbol {
    width: 52px;
    height: 52px;
    flex: 0 0 52px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 28px;
}
.recovery-icon {
    font-size: 64px;
}
.recovery-otp .form-control {
    flex: 1 1 0;
    width: 0;
    min-width: 0;
    height: 52px;
    background-image: none;
    padding: 0 !important;
}
@media (max-width: 575px) {
    .recovery-logo {
        height: 36px;
    }
}
</style>
